import type { ChoiceOption, FormDefinition, FormEntry, FormStep } from '@/api/types/Forms'
import {
	type FormStates,
	type StepState,
	getAddressState,
	getChoiceState,
	getDateState,
	getQuestionState,
	isDateComplete,
} from './stepState'
import { isStepVisible } from './visibility'

/**
 * The submission payload, built the way web-core's Quiz built it.
 *
 * Every string here reaches Ascend through provider-kapi's label and answer matching, so the
 * shapes are copied rather than improved: option titles as answers, an unpadded `M/D/YYYY`,
 * the address as an object, "Acknowledged" for a consent. Two departures from the web, both on
 * purpose: an INFO step sends nothing (it never did), and a step the patient never saw sends
 * nothing (the web still sent "Acknowledged" for a consent it had hidden).
 */
export const extractEntries = (
	definition: FormDefinition,
	states: FormStates
): FormEntry[] =>
	definition.steps
		.filter((step) => step.type !== 'INFO' && isStepVisible(step, states))
		.map((step) => ({
			questionId: step.id,
			question: step.label,
			answers: extractAnswers(step, states[step.id]),
		}))

const findOptionForId = (options: ChoiceOption[], id: string) =>
	options.find(
		(option) =>
			String(option.id) === id ||
			(option.subOptions ?? []).some((sub) => String(sub.id) === id)
	)

const extractChoices = (step: FormStep, state: StepState | undefined): string[] => {
	const choice = getChoiceState(state)
	const options = step.options ?? []

	// JavaScript iterates integer-like keys in ascending order, so the web sent answers sorted by
	// option id whatever the tapping order; sorting here keeps the two outputs the same.
	const selectedIds = Object.keys(choice).sort((a, b) => Number(a) - Number(b))

	return selectedIds.flatMap((id) => {
		const option = findOptionForId(options, id)
		if (!option) {
			return []
		}
		switch (option.type) {
			case 'EDITABLE':
				return [choice[id]?.value ?? '']
			case 'SELECTABLE':
				return [option.title]
			case 'EXPANDABLE_SELECT': {
				const sub = (option.subOptions ?? []).find((s) => String(s.id) === id)
				return sub ? [sub.title] : []
			}
		}
	})
}

const extractAnswers = (
	step: FormStep,
	state: StepState | undefined
): FormEntry['answers'] => {
	switch (step.type) {
		case 'CHOICE':
			return extractChoices(step, state)
		case 'QUESTION': {
			const text = getQuestionState(state)
			return text ? [text] : []
		}
		case 'CONSENT':
			return ['Acknowledged']
		case 'DATE': {
			const date = getDateState(state)
			return isDateComplete(date) ? [`${date.month}/${date.day}/${date.year}`] : []
		}
		case 'ADDRESS': {
			const address = getAddressState(state)
			return [
				{
					street1: address.street1,
					street2: address.street2,
					city: address.city,
					state: address.state,
					zip: address.zip,
				},
			]
		}
		case 'INFO':
			return []
	}
}
